/**
 * React Query v3 기반 공통 쿼리/뮤테이션 유틸리티
 *
 * 잔류물질안전정보시스템 v3의 모든 데이터 페칭 훅의 기반이 되는 핵심 모듈.
 * - QUERY_KEYS: 도메인별 쿼리 키 상수
 * - STALE_TIMES: 데이터 신선도 시간 상수
 * - useV2Query: useQuery 래퍼
 * - useV2Mutation: useMutation 래퍼
 * - useV2PaginatedQuery: 페이지네이션 응답 처리 쿼리 래퍼
 * - extractData / extractDataWithPagination: 응답 데이터 추출 유틸리티
 */
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { message } from 'antd';

// ═══════════════════════════════════════════════════════════
// 쿼리 키 상수
// ═══════════════════════════════════════════════════════════

/** 도메인별 쿼리 키 */
export const QUERY_KEYS = {
  PRD: 'prd',
  VD: 'vd',
  STD: 'std',
  DOC: 'doc',
  HOME: 'home',
  SEARCH: 'search',
  ADMIN: 'admin',
  COMMON: 'common',
};

// ═══════════════════════════════════════════════════════════
// 스테일 시간 상수
// ═══════════════════════════════════════════════════════════

/** 데이터 신선도 유지 시간 (밀리초) */
export const STALE_TIMES = {
  /** 30초 - 자주 변경되는 데이터 */
  SHORT: 30 * 1000,
  /** 2분 - 보통 빈도로 변경되는 데이터 */
  MEDIUM: 2 * 60 * 1000,
  /** 5분 - 일반적인 데이터 */
  STANDARD: 5 * 60 * 1000,
  /** 5분 - 장기 데이터 */
  LONG: 5 * 60 * 1000,
  /** 30분 - 거의 변경되지 않는 정적 데이터 */
  STATIC: 30 * 60 * 1000,
};

// ═══════════════════════════════════════════════════════════
// 응답 데이터 추출 유틸리티
// ═══════════════════════════════════════════════════════════

/**
 * API 응답에서 data 필드 추출
 * v2Client 인터셉터가 { success, data, ... } 형태로 반환하므로
 * data 필드만 추출한다.
 *
 * @param {Object} response - API 응답 객체
 * @returns {any} response.data
 */
export const extractData = (response) => {
  return response?.data;
};

/**
 * API 응답에서 data와 pagination을 함께 추출
 * 페이지네이션 API 응답의 표준 형태:
 * { success: true, data: { items: [...], pagination: { page, limit, total, totalPages } } }
 *
 * @param {Object} response - API 응답 객체
 * @returns {{ data: Array, pagination: Object }}
 */
export const extractDataWithPagination = (response) => {
  const rawData = response?.data;
  // rawData가 배열이면 직접 사용, 아니면 items/list 필드 추출
  const items = Array.isArray(rawData)
    ? rawData
    : (rawData?.items || rawData?.list || []);
  // pagination은 최상위 또는 data 내부에 있을 수 있음
  const pagination = response?.pagination || rawData?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };
  return { data: items, pagination };
};

// ═══════════════════════════════════════════════════════════
// 쿼리 훅
// ═══════════════════════════════════════════════════════════

/**
 * useQuery 래퍼 훅
 *
 * 표준화된 옵션으로 useQuery를 감싸 일관된 데이터 페칭 패턴을 제공한다.
 *
 * @param {Array|string} queryKey - 쿼리 키
 * @param {Function} queryFn - 데이터 조회 함수
 * @param {Object} [options={}] - useQuery 추가 옵션
 * @returns {Object} useQuery 반환 값
 */
export const useV2Query = (queryKey, queryFn, options = {}) => {
  return useQuery(queryKey, queryFn, {
    staleTime: STALE_TIMES.STANDARD,
    // 401 인증 오류는 재시도 불필요 (재시도해도 동일한 401 반환)
    // 로그인 후 queryClient.invalidateQueries()로 갱신
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * useMutation 래퍼 훅
 *
 * 성공/실패 시 표준 처리를 포함한 useMutation 래퍼.
 * onSuccess 시 지정된 쿼리를 무효화하여 자동으로 데이터를 갱신한다.
 *
 * @param {Function} mutationFn - 뮤테이션 함수
 * @param {Object} [options={}] - 추가 옵션
 * @param {string|string[]} [options.invalidateKeys] - 성공 시 무효화할 쿼리 키
 * @param {string} [options.successMessage] - 성공 시 표시할 메시지
 * @param {string} [options.errorMessage] - 실패 시 표시할 메시지
 * @param {Function} [options.onSuccess] - 성공 콜백
 * @param {Function} [options.onError] - 실패 콜백
 * @returns {Object} useMutation 반환 값
 */
export const useV2Mutation = (mutationFn, options = {}) => {
  const queryClient = useQueryClient();
  const {
    invalidateKeys,
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    ...restOptions
  } = options;

  return useMutation(mutationFn, {
    onSuccess: (data, variables, context) => {
      // 성공 메시지 표시
      if (successMessage) {
        message.success(successMessage);
      }

      // 관련 쿼리 무효화
      if (invalidateKeys) {
        const keys = Array.isArray(invalidateKeys)
          ? invalidateKeys
          : [invalidateKeys];
        keys.forEach((key) => {
          queryClient.invalidateQueries(key);
        });
      }

      // 사용자 정의 성공 콜백
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // 에러 메시지 표시
      const msg =
        errorMessage ||
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        '요청 처리 중 오류가 발생했습니다.';
      message.error(msg);

      // 사용자 정의 에러 콜백
      if (onError) {
        onError(error, variables, context);
      }
    },
    ...restOptions,
  });
};

/**
 * 페이지네이션 쿼리 래퍼 훅
 *
 * 서버 페이지네이션 응답을 자동으로 파싱하여
 * data(목록)와 pagination(페이지 정보)을 분리하여 반환한다.
 *
 * @param {Array|string} queryKey - 쿼리 키
 * @param {Function} queryFn - 데이터 조회 함수
 * @param {Object} [options={}] - useQuery 추가 옵션
 * @returns {Object} { data, pagination, isLoading, isError, error, refetch, ... }
 */
export const useV2PaginatedQuery = (queryKey, queryFn, options = {}) => {
  const query = useV2Query(queryKey, queryFn, {
    keepPreviousData: true,
    ...options,
  });

  const { data: rawData, ...rest } = query;
  const extracted = rawData ? extractDataWithPagination(rawData) : { data: [], pagination: null };

  return {
    ...rest,
    data: extracted.data,
    pagination: extracted.pagination,
  };
};
